import { Autocomplete, FormControl, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useField } from '@toolbox/form';
import { useMarketData } from '@widget/core';
import { Icon } from '@toolbox/components';
import ThemeProvider from '@toolbox/style/theme/common/ThemeProvider/ThemeProvider';
import { useAddressSuggestions } from './hooks/useAddressSuggestions';
import { AddressOption } from './components/AddressOption/AddressOption';
import { useDebounce } from '@toolbox/hooks';

import type { Suggestion } from '@widget/components/AddressSuggestField/types';
import type { UseAutocompleteProps } from '@mui/base/AutocompleteUnstyled/useAutocomplete';
import type { FieldPath } from '@toolbox/form';
import type { FC } from 'react';

type Values = Record<string, Suggestion>;
type AddressSuggestFieldProps = {
  label: string;
  name: string;
  required?: boolean;
  cityFieldName?: string;
  streetFieldName?: string;
  zipCodeFieldName?: string;
  noHouseNumberError?: string;
  requiredErrorText?: string;
};

type AutocompleteProps = UseAutocompleteProps<Suggestion, false, true, true>;

export const AddressSuggestField: FC<AddressSuggestFieldProps> = ({
  label,
  name,
  required,
  cityFieldName,
  streetFieldName,
  zipCodeFieldName,
  noHouseNumberError,
  requiredErrorText,
}) => {
  const { market } = useMarketData();
  const streetWithHouseRef = useRef<Suggestion | undefined>();
  const [inputValue, setInputValue] = useState('');
  const debouncedValue = useDebounce(inputValue, 300);
  const { clearErrors, setValue } = useFormContext();

  const validateStreetAndNumber = async (value: Suggestion) => {
    if (required && !value) {
      return requiredErrorText || 'Dieses Feld ist erforderlich';
    }

    if (!value?.houseNumber) {
      return noHouseNumberError || 'sie müssen die Hausnummer auswählen';
    }

    return true;
  };

  const { field, fieldState } = useField<Values, FieldPath<Values>>({
    name,
    rules: { required, validate: () => validateStreetAndNumber },
  });

  const { suggestions, loading, resetSuggestions, fetchSuggestions } = useAddressSuggestions(
    inputValue,
    { market },
  );

  const getOptionLabel: AutocompleteProps['getOptionLabel'] = (option) => {
    if (!option) {
      return '';
    }

    return [
      option.zipCode,
      option.city,
      [option.street, option.houseNumber].filter(Boolean).join(' '),
    ].join(', ');
  };

  const isOptionEqualToValue: AutocompleteProps['isOptionEqualToValue'] = (option, value) => {
    if (!value) {
      return false;
    }

    return option.id === value.id || option.formattedAddress === value.formattedAddress;
  };

  const handleInputChange: AutocompleteProps['onInputChange'] = async (event, value) => {
    setInputValue(value);
    clearErrors(name);
  };

  const changeDataWithRelatedFields = (value: Suggestion) => {
    field.onChange(value);
    streetWithHouseRef.current = value.houseNumber ? value : undefined;

    if (cityFieldName) {
      setValue(cityFieldName, value.city);
    }
    if (streetFieldName && value.houseNumber) {
      setValue(streetFieldName, [value.street, value.houseNumber].join(' '));
    }
    if (zipCodeFieldName) {
      setValue(zipCodeFieldName, value.zipCode);
    }
  };

  const handleChange: AutocompleteProps['onChange'] = (event, value) => {
    if (typeof value !== 'string') {
      changeDataWithRelatedFields(value);
    }
  };

  const handleBlur = () => {
    // Auto select when we type street number.
    if (streetWithHouseRef.current) {
      changeDataWithRelatedFields(streetWithHouseRef.current);

      const optionValue = getOptionLabel(streetWithHouseRef.current);
      setInputValue(optionValue);
    } else if (field.value) {
      const optionValue = getOptionLabel(field.value);

      if (inputValue !== optionValue) {
        setInputValue(optionValue);
      }
    } else {
      setInputValue('');
      resetSuggestions();
    }
  };

  useEffect(() => {
    (async () => {
      const nextSuggestions = await fetchSuggestions(debouncedValue, { type: 'street, address' });

      const matchedSuggestion = nextSuggestions
        .filter(({ type }) => type === 'address')
        .find((suggestion) => getOptionLabel(suggestion) === debouncedValue.trim());

      if (matchedSuggestion) {
        streetWithHouseRef.current = matchedSuggestion;
      }
    })();
  }, [debouncedValue]);

  return (
    <ThemeProvider>
      <FormControl fullWidth>
        <Autocomplete<Suggestion, false, true, true>
          inputValue={inputValue}
          value={field.value ?? null}
          options={suggestions}
          forcePopupIcon={false}
          disableClearable
          ListboxProps={{ style: { maxHeight: 170 } }}
          freeSolo
          loading={loading}
          loadingText="Laden... bitte warten"
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          onInputChange={handleInputChange}
          onChange={handleChange}
          onBlur={handleBlur}
          filterOptions={(options) => options}
          renderOption={(tagProps, suggestion, state) => (
            <AddressOption
              key={suggestion.id}
              tagProps={tagProps}
              suggestion={suggestion}
              state={state}
            />
          )}
          renderInput={({ InputProps, ...props }) => (
            <TextField
              label={label}
              InputProps={{ ...InputProps, endAdornment: <Icon type="location" /> }}
              error={fieldState.invalid}
              helperText={fieldState.invalid ? fieldState.error!.message : undefined}
              {...props}
            />
          )}
        />
      </FormControl>
    </ThemeProvider>
  );
};
